import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { computed, onUnmounted, ref } from 'vue';
import { openSelectId } from './useSelectGroup';
import { SelectRoot, SelectValue, SelectItem, SelectItemText, SelectItemIndicator } from 'reka-ui';
import { SelectTrigger, SelectContent } from 'Components/primitives/select';
import { Label } from 'Components/primitives/label';
import EnCheckbox from '../EnCheckbox';
import { X as CloseIcon, Check as CheckIcon } from 'lucide-vue-next';
import { useValidation } from 'Components/inputs/useValidation';
export default /*@__PURE__*/ _defineComponent({
    ...{ inheritAttrs: false },
    __name: 'EnSelectField',
    props: /*@__PURE__*/ _mergeModels({
        items: { type: Array, required: false, default: () => [] },
        label: { type: String, required: false },
        placeholder: { type: String, required: false },
        disabled: { type: Boolean, required: false, default: false },
        readonly: { type: Boolean, required: false, default: false },
        rules: { type: Array, required: false },
        name: { type: String, required: false },
        itemTitle: { type: String, required: false, default: 'label' },
        itemValue: { type: String, required: false, default: 'value' },
        multiple: { type: Boolean, required: false, default: false },
        clearable: { type: Boolean, required: false, default: false },
        staticId: { type: [String, Number, null], required: false, default: null },
        required: { type: Boolean, required: false, default: false },
        hint: { type: String, required: false },
        persistentHint: { type: Boolean, required: false, default: false }
    }, {
        "modelValue": { type: null },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue'], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const model = _useModel(__props, 'modelValue');
        const emit = __emit;
        const isDirty = ref(false);
        const { errorMessage, onBlur: onValidationBlur, } = useValidation(isDirty, props, model);
        const hasErrors = computed(() => {
            return !!errorMessage.value;
        });
        const labelComputed = computed(() => props.required ? `${props.label} *` : props.label);
        const hasValue = computed(() => {
            if (props.multiple)
                return Array.isArray(model.value) && model.value.length > 0;
            return model.value != null && model.value !== '';
        });
        function clearValue(event) {
            event.stopPropagation();
            model.value = props.multiple ? [] : null;
        }
        /**
         * Unique id for this select instance, used with the shared `openSelectId` ref
         * (see `useSelectGroup.ts`) to make sure only one EnSelectField is open at a time.
         * When this one opens it writes `selfId` into `openSelectId`, a select is only
         * considered open while `openSelectId` still points at its own `selfId`.
         */
        const selfId = Symbol();
        const isOpen = computed(() => openSelectId.value === selfId);
        const showHint = computed(() => !!props.hint && (props.persistentHint || isOpen.value));
        function handleOpenChange(val) {
            if (val) {
                openSelectId.value = selfId;
            }
            else if (openSelectId.value === selfId) {
                openSelectId.value = null;
            }
            if (!val) {
                isDirty.value = true;
                onValidationBlur();
            }
        }
        function onUpdateModelValue(value) {
            emit('update:modelValue', value);
            isDirty.value = true;
        }
        onUnmounted(() => {
            // If this select is unmounted while still open, clear the shared ref so it
            // doesn't stay pointing at a select that no longer exists (which would
            // block any other select from opening).
            if (openSelectId.value === selfId)
                openSelectId.value = null;
        });
        const titleKey = computed(() => props.itemTitle);
        const valueKey = computed(() => props.itemValue);
        function isItemSelected(item) {
            if (!props.multiple)
                return false;
            return Array.isArray(model.value) && model.value.includes(item[valueKey.value]);
        }
        /**
         * Puts every item into the same shape so the template stays simple.
         *
         * Items can come in as plain values (like `['A', 'B']`) or as objects
         * (like `[{ label, value }]`). Here we turn both into an object with a
         * title key and a value key, so the template can always read
         * `item[titleKey]` and `item[valueKey]` and never has to check the shape.
         */
        const normalizedItems = computed(() => (props.items || []).map((rawItem) => {
            if (rawItem && typeof rawItem === 'object') {
                return {
                    ...rawItem,
                    [titleKey.value]: rawItem[titleKey.value],
                    [valueKey.value]: rawItem[valueKey.value],
                };
            }
            return { [titleKey.value]: String(rawItem), [valueKey.value]: rawItem };
        }));
        const selectedTitle = computed(() => {
            if (!hasValue.value)
                return '';
            if (props.multiple) {
                return (model.value || [])
                    .map((value) => normalizedItems.value.find((item) => item[valueKey.value] === value)?.[titleKey.value])
                    .filter(Boolean)
                    .join(', ');
            }
            return normalizedItems.value.find((item) => item[valueKey.value] === model.value)?.[titleKey.value] ?? '';
        });
        const __returned__ = { props, model, emit, isDirty, errorMessage, onValidationBlur, hasErrors, labelComputed, hasValue, clearValue, selfId, isOpen, showHint, handleOpenChange, onUpdateModelValue, titleKey, valueKey, isItemSelected, normalizedItems, selectedTitle, get SelectRoot() { return SelectRoot; }, get SelectValue() { return SelectValue; }, get SelectItem() { return SelectItem; }, get SelectItemText() { return SelectItemText; }, get SelectItemIndicator() { return SelectItemIndicator; }, get SelectTrigger() { return SelectTrigger; }, get SelectContent() { return SelectContent; }, get Label() { return Label; }, get EnCheckbox() { return EnCheckbox; }, get CloseIcon() { return CloseIcon; }, get CheckIcon() { return CheckIcon; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
